<ng-template #panel>
  <aside
    class="flex h-full w-full flex-col border-l border-gray-200 bg-white shadow-xl"
    role="dialog"
    aria-label="Patient Quick View">
    <!-- TOP BAR: outside the scroll area so Close stays reachable at any content height -->
    <div
      class="flex h-16 shrink-0 items-center justify-between border-b border-gray-100 px-5">
      <h2 class="text-lg font-semibold text-gray-900">Patient Quick View</h2>

      <button
        type="button"
        class="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-700"
        aria-label="Close patient quick view"
        (click)="store.close()">
        <mat-icon svgIcon="x-outline" class="h-5! w-5!"></mat-icon>
      </button>
    </div>

    <div class="min-h-0 flex-1 overflow-y-auto">
      @if (store.failed()) {
        <div class="flex flex-col items-start gap-y-3 p-5">
          <p class="text-sm text-gray-700">Could not load patient details</p>

          <icaredx-button color="secondary" (clicked)="store.retry()">
            Retry
          </icaredx-button>
        </div>
      } @else {
        <div class="flex min-h-[13.5rem] gap-x-4 p-5">
          @if (store.loading()) {
            <icaredx-skeleton width="5rem" height="5rem" [circle]="true" />

            <div class="flex min-w-0 flex-1 flex-col gap-y-2 pt-1">
              <icaredx-skeleton width="60%" height="1.5rem" />
              <icaredx-skeleton width="45%" height="1rem" />
              <icaredx-skeleton width="50%" height="1rem" />
              <icaredx-skeleton width="70%" height="1rem" />
              <icaredx-skeleton width="55%" height="1rem" />
              <icaredx-skeleton width="65%" height="1rem" />
            </div>
          } @else if (store.patient(); as patient) {
            @if (patient.profileImageUrl) {
              <img
                [src]="patient.profileImageUrl"
                [alt]="displayName()"
                class="h-20 w-20 shrink-0 rounded-full object-cover" />
            } @else {
              <div
                class="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-blue-100 text-2xl font-semibold text-blue-700">
                {{ initials() }}
              </div>
            }

            <div class="flex min-w-0 flex-1 flex-col">
              <div class="flex items-start gap-x-2">
                <p
                  class="min-w-0 text-xl font-semibold break-words text-gray-900">
                  {{ displayName() }}
                </p>
                @if (isMinor()) {
                  <provider-portal-minor-indicator class="mt-1" />
                }
              </div>

              <p class="mt-1 text-sm text-gray-500">{{ dateOfBirthLine() }}</p>

              <div class="mt-2 flex items-center gap-x-2">
                <mat-icon
                  svgIcon="phone-outline"
                  class="h-5! w-5! shrink-0 text-gray-500!"></mat-icon>
                <span class="min-w-0 text-sm break-words text-gray-700">
                  {{ phone() }}
                </span>
              </div>

              <div class="mt-1 flex items-center gap-x-2">
                <mat-icon
                  svgIcon="envelope-outline"
                  class="h-5! w-5! shrink-0 text-gray-500!"></mat-icon>
                <span class="min-w-0 text-sm break-words text-gray-700">
                  {{ email() }}
                </span>
              </div>

              <div class="mt-2 flex items-center gap-x-2">
                <mat-icon
                  svgIcon="calendar-day"
                  class="h-5! w-5! shrink-0 text-gray-500!"></mat-icon>
                <span class="min-w-0 text-sm break-words text-gray-700">
                  Last visit: {{ lastVisitLabel() }}
                </span>
              </div>

              <div class="mt-1 flex items-center gap-x-2">
                <mat-icon
                  svgIcon="clock"
                  class="h-5! w-5! shrink-0"
                  [class]="
                    hasNextVisit() ? 'text-primary-700!' : 'text-gray-500!'
                  "></mat-icon>
                <span
                  class="min-w-0 text-sm break-words"
                  [class.text-primary-700]="hasNextVisit()"
                  [class.font-medium]="hasNextVisit()"
                  [class.text-gray-700]="!hasNextVisit()">
                  Next visit: {{ nextVisitLabel() }}
                </span>
              </div>

              @if (guardianState() !== 'hidden') {
                @let contact = guardian();
                @let retained = guardianState() === 'retained';

                <div class="mt-3 flex flex-col">
                  <p
                    class="text-sm font-semibold break-words"
                    [class.text-gray-900]="!retained"
                    [class.text-gray-400]="retained">
                    Guardian: {{ contact?.name || notAvailable }}
                  </p>
                  @if (retained) {
                    <p class="text-xs text-gray-500">
                      {{ retainedGuardianNote }}
                    </p>
                  }
                  @if (contact) {
                    <div class="mt-1 flex items-center gap-x-2">
                      <mat-icon
                        svgIcon="envelope-outline"
                        class="h-5! w-5! shrink-0"
                        [class]="
                          retained ? 'text-gray-400!' : 'text-gray-500!'
                        "></mat-icon>
                      <span
                        class="min-w-0 text-sm break-words"
                        [class.text-gray-700]="!retained"
                        [class.text-gray-400]="retained">
                        {{ contact.email }}
                      </span>
                    </div>
                    <div class="mt-1 flex items-center gap-x-2">
                      <mat-icon
                        svgIcon="phone-outline"
                        class="h-5! w-5! shrink-0"
                        [class]="
                          retained ? 'text-gray-400!' : 'text-gray-500!'
                        "></mat-icon>
                      <span
                        class="min-w-0 text-sm break-words"
                        [class.text-gray-700]="!retained"
                        [class.text-gray-400]="retained">
                        {{ contact.phone }}
                      </span>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>

        <div class="flex flex-wrap items-center gap-2 px-5 pb-4">
          <icaredx-button
            svgIcon="file-lines-outline"
            [disabled]="!store.ready()"
            (clicked)="store.openChart()">
            Open Chart
          </icaredx-button>

          <!-- A disabled button receives no mouse events, so the hint hangs off a wrapper. -->
          <span [matTooltip]="openVisitHint()" matTooltipPosition="above">
            <icaredx-button
              color="secondary"
              svgIcon="videocamera"
              [disabled]="!canOpenVisit() || store.startingVisit()"
              [loading]="store.startingVisit()"
              (clicked)="store.startVisit()">
              Open Visit
            </icaredx-button>
          </span>

          <icaredx-button
            color="secondary"
            svgIcon="messages-outline"
            [disabled]="!store.ready()"
            (clicked)="store.message()">
            Message
          </icaredx-button>
        </div>

        @if (visitErrorMessage(); as message) {
          <div class="flex items-center gap-x-3 px-5 pb-4">
            <p class="text-sm text-red-600">{{ message }}</p>

            @if (canRetryVisit()) {
              <icaredx-button color="secondary" (clicked)="store.startVisit()">
                Retry
              </icaredx-button>
            }
          </div>
        }

        <div class="border-t border-gray-100"></div>

        <div class="px-5 pt-5">
          <section
            class="min-h-[9rem] rounded-lg bg-gray-50 p-3"
            aria-label="AI Summary">
            <div class="flex items-start justify-between gap-x-3">
              <div class="flex items-center gap-x-2">
                <mat-icon
                  svgIcon="wand-magic-sparkles"
                  class="h-5! w-5! shrink-0 text-primary-700!"></mat-icon>
                <h3 class="text-sm font-semibold text-gray-900">AI Summary</h3>
              </div>

              @if (summary().kind === 'idle' || summary().kind === 'generating') {
                <icaredx-button
                  color="primary-outline"
                  svgIcon="wand-magic-sparkles"
                  padding="px-3 py-1.5"
                  [disabled]="!store.ready() || summary().kind === 'generating'"
                  (clicked)="store.generateSummary()">
                  {{
                    summary().kind === 'generating'
                      ? 'Generating…'
                      : 'Generate summary'
                  }}
                </icaredx-button>
              }
            </div>

            @switch (summary().kind) {
              @case ('generating') {
                <div class="mt-3 flex flex-col gap-y-2">
                  <icaredx-skeleton height="0.75rem" />
                  <icaredx-skeleton height="0.75rem" />
                  <icaredx-skeleton width="60%" height="0.75rem" />
                </div>
              }
              @case ('ready') {
                <!-- Interpolated, never innerHTML: the text comes from a clinical note. -->
                <p class="mt-3 text-sm whitespace-pre-line text-gray-800">
                  {{ summaryText() }}
                </p>
                <p class="mt-3 text-xs text-gray-500">{{ summaryCaption() }}</p>
              }
              @case ('empty') {
                <p class="mt-3 text-sm text-gray-500">No subjective notes yet</p>
              }
              @case ('failed') {
                <p class="mt-3 text-sm text-gray-700">
                  Couldn't generate the summary
                </p>
                <div class="mt-2">
                  <icaredx-button
                    color="secondary"
                    padding="px-3 py-1.5"
                    (clicked)="store.generateSummary()">
                    Retry
                  </icaredx-button>
                </div>
              }
              @case ('unavailable') {
                <p class="mt-3 text-sm text-gray-500">
                  Summary unavailable for this note
                </p>
              }
            }
          </section>
        </div>

        <div class="grid grid-cols-2 gap-3 p-5">
          <provider-portal-patient-quick-view-card
            class="min-h-[14rem]"
            title="Chief Complaints"
            svgIcon="exclamation-outline"
            iconClass="text-red-600!"
            [loading]="store.loading() || store.complaints.loading()"
            [failed]="store.complaints.failed()"
            [isEmpty]="store.complaints.isEmpty()"
            (retried)="store.complaints.retry()"
            (viewAll)="store.openChart()">
            <ul class="flex flex-col gap-y-1.5">
              @for (symptom of store.complaints.items(); track symptom.id) {
                <li class="flex items-center gap-x-2">
                  <span
                    class="h-1.5 w-1.5 shrink-0 rounded-full"
                    [class]="statusDotClass(symptom)"></span>
                  <span class="min-w-0 flex-1 truncate text-sm text-gray-900">
                    {{ symptom.text }}
                  </span>
                  <span class="shrink-0 text-xs" [class]="statusClass(symptom)">
                    {{ statusLabel(symptom) }}
                  </span>
                </li>
              }
            </ul>
          </provider-portal-patient-quick-view-card>

          <provider-portal-patient-quick-view-card
            class="min-h-[14rem]"
            title="Root Causes"
            svgIcon="flag-outline"
            iconClass="text-yellow-800!"
            [loading]="store.loading() || store.rootCauses.loading()"
            [failed]="store.rootCauses.failed()"
            [isEmpty]="store.rootCauses.isEmpty()"
            (retried)="store.rootCauses.retry()"
            (viewAll)="store.openChart()">
            <ul class="flex flex-col gap-y-1.5">
              @for (rootCause of store.rootCauses.items(); track rootCause.id) {
                <li class="flex items-center gap-x-2">
                  <span
                    class="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-700"></span>
                  <span class="min-w-0 flex-1 truncate text-sm text-gray-900">
                    {{ rootCause.title }}
                  </span>
                </li>
              }
            </ul>
          </provider-portal-patient-quick-view-card>

          <provider-portal-patient-quick-view-card
            class="min-h-[19rem]"
            title="Medications"
            svgIcon="medications-outline"
            iconClass="text-primary-700!"
            [loading]="store.loading() || store.medicationList.loading()"
            [failed]="store.medicationList.failed()"
            [isEmpty]="store.medicationList.isEmpty()"
            (retried)="store.medicationList.retry()"
            (viewAll)="store.openChart()">
            <ul class="flex flex-col gap-y-1.5">
              @for (
                medication of store.medicationList.items();
                track medication.id
              ) {
                <li class="flex min-w-0 flex-col">
                  <span class="truncate text-sm text-gray-900">
                    {{ medicationName(medication) }}
                  </span>
                  <span class="truncate text-xs text-gray-500">
                    {{ intakeLabel(medication) }}
                  </span>
                </li>
              }
            </ul>
          </provider-portal-patient-quick-view-card>

          <provider-portal-patient-quick-view-card
            class="min-h-[19rem]"
            title="Supplements"
            svgIcon="supplements-outline"
            iconClass="text-green-600!"
            [loading]="store.loading() || store.supplementList.loading()"
            [failed]="store.supplementList.failed()"
            [isEmpty]="store.supplementList.isEmpty()"
            (retried)="store.supplementList.retry()"
            (viewAll)="store.openChart()">
            <ul class="flex flex-col gap-y-1.5">
              @for (
                supplement of store.supplementList.items();
                track supplement.id
              ) {
                <li class="flex min-w-0 flex-col">
                  <span class="truncate text-sm text-gray-900">
                    {{ supplement.name }}
                  </span>
                  <span class="truncate text-xs text-gray-500">
                    {{ intakeLabel(supplement) }}
                  </span>
                </li>
              }
            </ul>
          </provider-portal-patient-quick-view-card>
        </div>

        <div class="flex flex-col gap-3 px-5 pb-5">
          <provider-portal-patient-quick-view-card
            class="min-h-[7rem]"
            title="Billing"
            svgIcon="receipt-outline"
            iconClass="text-primary-700!"
            [showViewAll]="false"
            [loading]="store.loading() || store.billingSection.loading()"
            [failed]="store.billingSection.failed()"
            (retried)="store.billingSection.retry()">
            @if (hasBalanceDue()) {
              <button
                type="button"
                class="flex w-full items-center gap-x-2 rounded-lg bg-red-50 px-3 py-2 text-left hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600"
                [attr.aria-label]="balanceAriaLabel()"
                (click)="store.openBilling()">
                <mat-icon
                  svgIcon="coin"
                  class="h-5! w-5! shrink-0 text-red-600!"></mat-icon>
                <span class="min-w-0 flex-1 text-sm font-medium text-red-700">
                  {{ balanceLabel() }}
                </span>
                <mat-icon
                  svgIcon="chevron-right"
                  class="h-5! w-5! shrink-0 text-red-600!"></mat-icon>
              </button>
            } @else {
              <p class="text-sm text-gray-500">{{ balanceLabel() }}</p>
            }
          </provider-portal-patient-quick-view-card>

          <provider-portal-patient-quick-view-card
            class="min-h-[9.5rem]"
            title="Recent Activity"
            svgIcon="clock"
            iconClass="text-primary-700!"
            emptyText="No recent activity"
            [showViewAll]="false"
            [loading]="store.loading() || store.activity.loading()"
            [failed]="store.activity.failed()"
            [isEmpty]="store.activity.isEmpty()"
            (retried)="store.activity.retry()">
            <ul class="flex flex-col gap-y-2">
              @for (item of store.activity.items(); track $index) {
                <li class="flex items-center gap-x-2">
                  <mat-icon
                    [svgIcon]="activityIcon(item)"
                    class="h-5! w-5! shrink-0 text-gray-500!"></mat-icon>
                  <span class="min-w-0 flex-1 text-sm text-gray-900">
                    {{ item.line }}
                  </span>
                  <span class="shrink-0 text-xs text-gray-500">
                    {{
                      item.occurredAtUtc
                        | zonedDate: activityTimeFormat(item) : store.displayTimeZone()
                    }}
                  </span>
                </li>
              }
            </ul>
          </provider-portal-patient-quick-view-card>
        </div>
      }
    </div>
  </aside>
</ng-template>
